/**
 * LiveKit Context
 * Manages LiveKit room connections for channel voice sessions
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { Room, RoomEvent, Track, RemoteTrack, RemoteParticipant, LocalTrackPublication } from 'livekit-client';
import { apiClient } from '@/services/api.client';
import { socketClient } from '@/services/socket.client';
import { useUserSettings } from '@/hooks/useUserSettings';
import { soundService, type SoundType } from '@/services/sound.service';
import { toast } from 'sonner';
import { asApiError } from '@/utils/error.util';

interface LiveKitParticipant {
  userId: string;
  displayName: string;
  isMuted: boolean;
  isSpeaking: boolean;
}

interface ScreenShare {
  participantId: string;
  participantName: string;
  track: RemoteTrack;
}

interface LiveKitState {
  room: Room | null;
  channelId: string | null;
  voiceRoomId: string | null;
  dmThreadId: string | null;
  sessionId: string | null;
  participants: LiveKitParticipant[];
  screenShares: ScreenShare[];
  isConnecting: boolean;
  isConnected: boolean;
  isMuted: boolean;
  isDeafened: boolean;
  isScreenSharing: boolean;
  error: string | null;
}

interface LiveKitContextValue {
  state: LiveKitState;
  joinChannelVoice: (channelId: string) => Promise<void>;
  leaveChannelVoice: () => Promise<void>;
  joinVoiceRoom: (roomId: string) => Promise<void>;
  leaveVoiceRoom: () => Promise<void>;
  joinDMCall: (dmThreadId: string, sessionId: string, token: string, liveKitUrl: string) => Promise<void>;
  leaveDMCall: () => Promise<void>;
  toggleMute: () => void;
  toggleDeafen: () => void;
  toggleScreenShare: () => Promise<void>;
}

const LiveKitContext = createContext<LiveKitContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components -- context hook co-located with its Provider (fast-refresh only)
export const useLiveKit = () => {
  const context = useContext(LiveKitContext);
  if (!context) {
    throw new Error('useLiveKit must be used within LiveKitProvider');
  }
  return context;
};

export const LiveKitProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: userSettings } = useUserSettings();

  const [state, setState] = useState<LiveKitState>({
    room: null,
    channelId: null,
    voiceRoomId: null,
    dmThreadId: null,
    sessionId: null,
    participants: [],
    screenShares: [],
    isConnecting: false,
    isConnected: false,
    isMuted: false,
    isDeafened: false,
    isScreenSharing: false,
    error: null,
  });

  const roomRef = useRef<Room | null>(null);
  const audioElementsRef = useRef<Map<string, HTMLAudioElement>>(new Map());

  /**
   * Get list of participants from room
   */
  const getParticipantsList = (room: Room): LiveKitParticipant[] => {
    const participants: LiveKitParticipant[] = [];

    // Add local participant
    const local = room.localParticipant;
    participants.push({
      userId: local.identity,
      displayName: local.name || local.identity,
      isMuted: !local.isMicrophoneEnabled,
      isSpeaking: local.isSpeaking,
    });

    // Add remote participants
    room.remoteParticipants.forEach((participant) => {
      participants.push({
        userId: participant.identity,
        displayName: participant.name || participant.identity,
        isMuted: !participant.isMicrophoneEnabled,
        isSpeaking: participant.isSpeaking,
      });
    });

    return participants;
  };

  /**
   * Handle remote track subscription
   */
  const handleTrackSubscribed = useCallback(
    (track: RemoteTrack, _publication: unknown, participant: RemoteParticipant) => {
      if (track.kind === Track.Kind.Audio) {
        // Create audio element for remote participant
        const audioElement = track.attach();
        audioElement.autoplay = true;
        audioElementsRef.current.set(participant.identity, audioElement);

        console.log('🔊 Subscribed to audio track from:', participant.identity);
      } else if (track.kind === Track.Kind.Video && track.source === Track.Source.ScreenShare) {
        console.log('🖥️ Subscribed to screen share from:', participant.identity);

        // Add screen share to state
        setState((prev) => ({
          ...prev,
          screenShares: [
            ...prev.screenShares,
            {
              participantId: participant.identity,
              participantName: participant.name || participant.identity,
              track: track,
            },
          ],
        }));
      }

      // Update participants list
      setState((prev) => ({
        ...prev,
        participants: getParticipantsList(roomRef.current!),
      }));
    },
    []
  );

  /**
   * Handle remote track unsubscription
   */
  const handleTrackUnsubscribed = useCallback(
    (track: RemoteTrack, _publication: unknown, participant: RemoteParticipant) => {
      if (track.kind === Track.Kind.Audio) {
        // Cleanup audio element
        const audioElement = audioElementsRef.current.get(participant.identity);
        if (audioElement) {
          audioElement.pause();
          audioElement.srcObject = null;
          audioElementsRef.current.delete(participant.identity);
        }

        console.log('🔇 Unsubscribed from audio track:', participant.identity);
      } else if (track.kind === Track.Kind.Video && track.source === Track.Source.ScreenShare) {
        console.log('🖥️ Unsubscribed from screen share:', participant.identity);

        // Remove screen share from state
        setState((prev) => ({
          ...prev,
          screenShares: prev.screenShares.filter(
            (share) => share.participantId !== participant.identity
          ),
        }));
      }

      // Update participants list
      setState((prev) => ({
        ...prev,
        participants: getParticipantsList(roomRef.current!),
      }));
    },
    []
  );

  /**
   * Handle participant connected
   */
  const handleParticipantConnected = useCallback((participant: RemoteParticipant) => {
    console.log('👤 Participant connected:', participant.identity);
    setState((prev) => ({
      ...prev,
      participants: getParticipantsList(roomRef.current!),
    }));
  }, []);

  /**
   * Handle participant disconnected
   */
  const handleParticipantDisconnected = useCallback((participant: RemoteParticipant) => {
    console.log('👤 Participant disconnected:', participant.identity);

    // Cleanup audio element
    const audioElement = audioElementsRef.current.get(participant.identity);
    if (audioElement) {
      audioElement.pause();
      audioElement.srcObject = null;
      audioElementsRef.current.delete(participant.identity);
    }

    setState((prev) => ({
      ...prev,
      participants: getParticipantsList(roomRef.current!),
    }));
  }, []);

  /**
   * Handle disconnection from room
   */
  const handleDisconnected = useCallback(() => {
    console.log('🔌 Disconnected from LiveKit room');

    // Cleanup
    audioElementsRef.current.forEach((audio) => {
      audio.pause();
      audio.srcObject = null;
    });
    audioElementsRef.current.clear();

    setState({
      room: null,
      channelId: null,
      voiceRoomId: null,
      dmThreadId: null,
      sessionId: null,
      participants: [],
      screenShares: [],
      isConnecting: false,
      isConnected: false,
      isMuted: false,
      isDeafened: false,
      isScreenSharing: false,
      error: 'Disconnected from voice session',
    });

    roomRef.current = null;
  }, []);

  /**
   * Handle local track published
   */
  const handleLocalTrackPublished = useCallback((publication: LocalTrackPublication) => {
    console.log('📤 Local track published:', publication.kind, publication.source);

    // Update screen sharing state when screen share track is published
    if (publication.source === 'screen_share') {
      console.log('✅ Screen share started');
      setState((prev) => ({ ...prev, isScreenSharing: true }));
    }
  }, []);

  /**
   * Handle local track unpublished
   */
  const handleLocalTrackUnpublished = useCallback((publication: LocalTrackPublication) => {
    console.log('📤 Local track unpublished:', publication.kind, publication.source);

    // Update screen sharing state when screen share track is unpublished
    if (publication.source === 'screen_share') {
      console.log('🛑 Screen share stopped');
      setState((prev) => ({ ...prev, isScreenSharing: false }));
    }
  }, []);

  /**
   * Join a channel voice session
   */
  const joinChannelVoice = useCallback(async (channelId: string) => {
    try {
      setState((prev) => ({ ...prev, isConnecting: true, error: null }));

      // Get LiveKit token from backend
      const response = await apiClient.post<{
        session: { id: string; roomId?: string };
        token: string;
        liveKitUrl: string;
      }>(`/voice/channels/${channelId}/join`);

      const { session, token, liveKitUrl } = response.data;

      // Create LiveKit room
      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        videoCaptureDefaults: {
          resolution: { width: 1280, height: 720 },
        },
      });

      roomRef.current = room;

      // Set up event handlers
      room.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);
      room.on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);
      room.on(RoomEvent.ParticipantConnected, handleParticipantConnected);
      room.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
      room.on(RoomEvent.Disconnected, handleDisconnected);
      room.on(RoomEvent.LocalTrackPublished, handleLocalTrackPublished);
      room.on(RoomEvent.LocalTrackUnpublished, handleLocalTrackUnpublished);

      // Connect to room
      await room.connect(liveKitUrl, token);

      // Enable microphone with user settings
      await room.localParticipant.setMicrophoneEnabled(true, {
        deviceId: userSettings?.voice_input_device || undefined,
        echoCancellation: userSettings?.echo_cancellation ?? true,
        noiseSuppression: userSettings?.noise_suppression ?? true,
        autoGainControl: userSettings?.auto_gain_control ?? true,
      });

      // Notify via WebSocket (if socket client has emit method)
      if (typeof (socketClient as unknown as { emit?: (event: string, payload: unknown) => void }).emit === 'function') {
        (socketClient as unknown as { emit: (event: string, payload: unknown) => void }).emit('voice:channel-join', {
          channelId,
          sessionId: session.id,
        });
      }

      setState((prev) => ({
        ...prev,
        room,
        channelId,
        sessionId: session.id,
        isConnecting: false,
        isConnected: true,
        participants: getParticipantsList(room),
      }));

      // Play join sound from user settings
      await soundService.playNotification(
        (userSettings?.voice_join_sound || 'voiceJoin') as SoundType,
        userSettings?.voice_join_volume ?? 50
      );

      console.log('✅ Connected to LiveKit room:', session.roomId);
    } catch (error) {
      console.error('Error joining channel voice:', error);
      const errorMsg = asApiError(error).response?.data?.error || 'Грешка при свързване с гласов канал';
      toast.error(errorMsg);
      setState((prev) => ({
        ...prev,
        isConnecting: false,
        error: errorMsg,
      }));
    }
  }, [userSettings, handleTrackSubscribed, handleTrackUnsubscribed, handleParticipantConnected, handleParticipantDisconnected, handleDisconnected, handleLocalTrackPublished, handleLocalTrackUnpublished]);

  /**
   * Leave channel voice session
   */
  const leaveChannelVoice = useCallback(async () => {
    const { channelId, sessionId, room } = state;

    if (!channelId || !room) return;

    try {
      // Disconnect from LiveKit room
      await room.disconnect();

      // Cleanup audio elements
      audioElementsRef.current.forEach((audio) => {
        audio.pause();
        audio.srcObject = null;
      });
      audioElementsRef.current.clear();

      // Leave session on backend
      await apiClient.delete(`/voice/channels/${channelId}/leave`);

      // Notify via WebSocket (if socket client has emit method)
      if (sessionId && typeof (socketClient as unknown as { emit?: (event: string, payload: unknown) => void }).emit === 'function') {
        (socketClient as unknown as { emit: (event: string, payload: unknown) => void }).emit('voice:channel-leave', {
          channelId,
          sessionId,
        });
      }

      // Play leave sound
      await soundService.playNotification(
        (userSettings?.voice_leave_sound || 'voiceLeave') as SoundType,
        userSettings?.voice_leave_volume ?? 50
      );

      setState({
        room: null,
        channelId: null,
        voiceRoomId: null,
        dmThreadId: null,
        sessionId: null,
        participants: [],
        screenShares: [],
        isConnecting: false,
        isConnected: false,
        isMuted: false,
        isDeafened: false,
        isScreenSharing: false,
        error: null,
      });

      roomRef.current = null;

      console.log('✅ Left channel voice');
    } catch (error) {
      console.error('Error leaving channel voice:', error);
    }
  }, [state, userSettings]);

  /**
   * Join a voice room session
   */
  const joinVoiceRoom = useCallback(async (voiceRoomId: string) => {
    try {
      setState((prev) => ({ ...prev, isConnecting: true, error: null }));

      const response = await apiClient.post<{
        session: { id: string; roomId?: string };
        token: string;
        liveKitUrl: string;
      }>(`/voice/rooms/${voiceRoomId}/join`);

      const { session, token, liveKitUrl } = response.data;

      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        videoCaptureDefaults: { resolution: { width: 1280, height: 720 } },
      });

      roomRef.current = room;

      room.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);
      room.on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);
      room.on(RoomEvent.ParticipantConnected, handleParticipantConnected);
      room.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
      room.on(RoomEvent.Disconnected, handleDisconnected);
      room.on(RoomEvent.LocalTrackPublished, handleLocalTrackPublished);
      room.on(RoomEvent.LocalTrackUnpublished, handleLocalTrackUnpublished);

      await room.connect(liveKitUrl, token);

      await room.localParticipant.setMicrophoneEnabled(true, {
        deviceId: userSettings?.voice_input_device || undefined,
        echoCancellation: userSettings?.echo_cancellation ?? true,
        noiseSuppression: userSettings?.noise_suppression ?? true,
        autoGainControl: userSettings?.auto_gain_control ?? true,
      });

      if (typeof (socketClient as unknown as { emit?: (event: string, payload: unknown) => void }).emit === 'function') {
        (socketClient as unknown as { emit: (event: string, payload: unknown) => void }).emit('voice:room-join', {
          voiceRoomId,
          sessionId: session.id,
        });
      }

      setState((prev) => ({
        ...prev,
        room,
        voiceRoomId,
        channelId: null,
        sessionId: session.id,
        isConnecting: false,
        isConnected: true,
        participants: getParticipantsList(room),
      }));

      await soundService.playNotification(
        (userSettings?.voice_join_sound || 'voiceJoin') as SoundType,
        userSettings?.voice_join_volume ?? 50
      );
    } catch (error) {
      console.error('Error joining voice room:', error);
      const errorMsg = asApiError(error).response?.data?.error || 'Грешка при свързване с гласовата стая';
      toast.error(errorMsg);
      setState((prev) => ({
        ...prev,
        isConnecting: false,
        error: errorMsg,
      }));
    }
  }, [userSettings, handleTrackSubscribed, handleTrackUnsubscribed, handleParticipantConnected, handleParticipantDisconnected, handleDisconnected, handleLocalTrackPublished, handleLocalTrackUnpublished]);

  /**
   * Leave voice room session
   */
  const leaveVoiceRoom = useCallback(async () => {
    const { voiceRoomId, sessionId, room } = state;

    if (!voiceRoomId || !room) return;

    try {
      await room.disconnect();

      audioElementsRef.current.forEach((audio) => {
        audio.pause();
        audio.srcObject = null;
      });
      audioElementsRef.current.clear();

      await apiClient.delete(`/voice/rooms/${voiceRoomId}/leave`);

      if (sessionId && typeof (socketClient as unknown as { emit?: (event: string, payload: unknown) => void }).emit === 'function') {
        (socketClient as unknown as { emit: (event: string, payload: unknown) => void }).emit('voice:room-leave', {
          voiceRoomId,
          sessionId,
        });
      }

      await soundService.playNotification(
        (userSettings?.voice_leave_sound || 'voiceLeave') as SoundType,
        userSettings?.voice_leave_volume ?? 50
      );

      setState({
        room: null,
        channelId: null,
        voiceRoomId: null,
        dmThreadId: null,
        sessionId: null,
        participants: [],
        screenShares: [],
        isConnecting: false,
        isConnected: false,
        isMuted: false,
        isDeafened: false,
        isScreenSharing: false,
        error: null,
      });

      roomRef.current = null;
    } catch (error) {
      console.error('Error leaving voice room:', error);
    }
  }, [state, userSettings]);

  /**
   * Join a DM voice call via LiveKit
   */
  const joinDMCall = useCallback(async (dmThreadId: string, sessionId: string, token: string, liveKitUrl: string) => {
    try {
      setState((prev) => ({ ...prev, isConnecting: true, error: null }));

      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        videoCaptureDefaults: { resolution: { width: 1280, height: 720 } },
      });

      roomRef.current = room;

      room.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);
      room.on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);
      room.on(RoomEvent.ParticipantConnected, handleParticipantConnected);
      room.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
      room.on(RoomEvent.Disconnected, handleDisconnected);
      room.on(RoomEvent.LocalTrackPublished, handleLocalTrackPublished);
      room.on(RoomEvent.LocalTrackUnpublished, handleLocalTrackUnpublished);

      await room.connect(liveKitUrl, token);

      await room.localParticipant.setMicrophoneEnabled(true, {
        deviceId: userSettings?.voice_input_device || undefined,
        echoCancellation: userSettings?.echo_cancellation ?? true,
        noiseSuppression: userSettings?.noise_suppression ?? true,
        autoGainControl: userSettings?.auto_gain_control ?? true,
      });

      // Join socket room for session events
      const socket = socketClient.getSocket();
      if (socket) {
        socket.emit('voice:join', { sessionId });
      }

      setState((prev) => ({
        ...prev,
        room,
        dmThreadId,
        channelId: null,
        voiceRoomId: null,
        sessionId,
        isConnecting: false,
        isConnected: true,
        participants: getParticipantsList(room),
      }));

      await soundService.playNotification(
        (userSettings?.voice_join_sound || 'voiceJoin') as SoundType,
        userSettings?.voice_join_volume ?? 50
      );
      console.log('✅ Connected to DM call via LiveKit, session:', sessionId);
    } catch (error) {
      console.error('Error joining DM call:', error);
      const errorMsg = asApiError(error).response?.data?.error || 'Грешка при свързване с обаждане';
      toast.error(errorMsg);
      setState((prev) => ({
        ...prev,
        isConnecting: false,
        error: errorMsg,
      }));
    }
  }, [userSettings, handleTrackSubscribed, handleTrackUnsubscribed, handleParticipantConnected, handleParticipantDisconnected, handleDisconnected, handleLocalTrackPublished, handleLocalTrackUnpublished]);

  /**
   * Leave a DM voice call
   */
  const leaveDMCall = useCallback(async () => {
    const { dmThreadId, sessionId, room } = state;

    if (!dmThreadId || !room) return;

    try {
      await room.disconnect();

      audioElementsRef.current.forEach((audio) => {
        audio.pause();
        audio.srcObject = null;
      });
      audioElementsRef.current.clear();

      // Leave session on backend
      await apiClient.delete(`/voice/calls/${sessionId}`);

      // Notify via WebSocket
      const socket = socketClient.getSocket();
      if (socket && sessionId) {
        socket.emit('voice:leave', { sessionId });
      }

      await soundService.playNotification(
        (userSettings?.voice_leave_sound || 'voiceLeave') as SoundType,
        userSettings?.voice_leave_volume ?? 50
      );

      setState({
        room: null,
        channelId: null,
        voiceRoomId: null,
        dmThreadId: null,
        sessionId: null,
        participants: [],
        screenShares: [],
        isConnecting: false,
        isConnected: false,
        isMuted: false,
        isDeafened: false,
        isScreenSharing: false,
        error: null,
      });

      roomRef.current = null;
      console.log('✅ Left DM call');
    } catch (error) {
      console.error('Error leaving DM call:', error);
    }
  }, [state, userSettings]);

  /**
   * Toggle microphone mute
   */
  const toggleMute = useCallback(() => {
    const { room } = state;
    if (!room) return;

    const newMutedState = !state.isMuted;
    room.localParticipant.setMicrophoneEnabled(!newMutedState);

    setState((prev) => ({ ...prev, isMuted: newMutedState }));
  }, [state]);

  /**
   * Toggle deafen (mute microphone AND mute audio output)
   */
  const toggleDeafen = useCallback(() => {
    const { room } = state;
    if (!room) return;

    const newDeafenedState = !state.isDeafened;

    // When deafened: mute mic AND mute all remote audio
    if (newDeafenedState) {
      // Mute microphone
      room.localParticipant.setMicrophoneEnabled(false);

      // Mute all remote audio elements
      audioElementsRef.current.forEach((audio) => {
        audio.muted = true;
      });

      setState((prev) => ({ ...prev, isDeafened: true, isMuted: true }));
    } else {
      // Unmute all remote audio elements
      audioElementsRef.current.forEach((audio) => {
        audio.muted = false;
      });

      setState((prev) => ({ ...prev, isDeafened: false }));
      // Note: Keep isMuted state as is - user can control mic separately
    }
  }, [state]);

  /**
   * Toggle screen sharing
   */
  const toggleScreenShare = useCallback(async () => {
    if (!roomRef.current) return;

    try {
      // Use the room's current state instead of React state to avoid closure issues
      const currentlySharing = roomRef.current.localParticipant.isScreenShareEnabled;

      if (currentlySharing) {
        // Stop screen sharing
        console.log('🛑 Stopping screen share...');
        await roomRef.current.localParticipant.setScreenShareEnabled(false);
      } else {
        // Start screen sharing
        console.log('🖥️ Starting screen share...');
        await roomRef.current.localParticipant.setScreenShareEnabled(true);
      }
    } catch (error) {
      console.error('Error toggling screen share:', error);
      setState((prev) => ({
        ...prev,
        error: 'Failed to toggle screen share',
      }));
    }
  }, []);


  /**
   * Re-register voice session on socket reconnect
   * If LiveKit is still connected but socket dropped and reconnected,
   * re-emit join event so the backend knows we're still in the session
   */
  useEffect(() => {
    const handleSocketReconnect = () => {
      if (!state.isConnected || !state.sessionId) return;

      const socket = socketClient.getSocket();
      if (!socket) return;

      if (state.channelId) {
        socket.emit('voice:channel-join', { channelId: state.channelId, sessionId: state.sessionId });
        console.log('🔄 Re-registered channel voice session after socket reconnect');
      } else if (state.voiceRoomId) {
        socket.emit('voice:room-join', { voiceRoomId: state.voiceRoomId, sessionId: state.sessionId });
        console.log('🔄 Re-registered voice room session after socket reconnect');
      } else if (state.dmThreadId) {
        socket.emit('voice:join', { sessionId: state.sessionId });
        console.log('🔄 Re-registered DM call session after socket reconnect');
      }
    };

    socketClient.onConnect(handleSocketReconnect);
    return () => socketClient.offConnect(handleSocketReconnect);
  }, [state.isConnected, state.sessionId, state.channelId, state.voiceRoomId, state.dmThreadId]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    const audioElements = audioElementsRef.current;
    return () => {
      if (roomRef.current) {
        roomRef.current.disconnect();
      }
      audioElements.forEach((audio) => {
        audio.pause();
        audio.srcObject = null;
      });
      audioElements.clear();
    };
  }, []);

  /**
   * Push-to-talk keyboard handler
   */
  useEffect(() => {
    if (!userSettings?.push_to_talk_enabled || !state.isConnected || !state.room) {
      return;
    }

    const pttKey = userSettings.push_to_talk_key;
    const room = state.room;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if the correct key is pressed and not already held down
      if (event.code === pttKey && !event.repeat) {
        // Unmute when PTT key is pressed
        room.localParticipant.setMicrophoneEnabled(true);
        setState((prev) => ({ ...prev, isMuted: false }));
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      // Mute when PTT key is released
      if (event.code === pttKey) {
        room.localParticipant.setMicrophoneEnabled(false);
        setState((prev) => ({ ...prev, isMuted: true }));
      }
    };

    // Add event listeners
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    // Set initial muted state for PTT mode (muted by default). This mirrors the
    // imperative mic mute above, so it must run inside the effect, not during render.
    room.localParticipant.setMicrophoneEnabled(false);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs UI state with imperative mic mute on PTT-mode entry
    setState((prev) => ({ ...prev, isMuted: true }));

    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [userSettings?.push_to_talk_enabled, userSettings?.push_to_talk_key, state.isConnected, state.room]);

  const value: LiveKitContextValue = {
    state,
    joinChannelVoice,
    leaveChannelVoice,
    joinVoiceRoom,
    leaveVoiceRoom,
    joinDMCall,
    leaveDMCall,
    toggleMute,
    toggleDeafen,
    toggleScreenShare,
  };

  return <LiveKitContext.Provider value={value}>{children}</LiveKitContext.Provider>;
};
